'use client';

import { usePathname } from 'next/navigation';
import { needsDataRequirement } from 'src/utils/routeDataRequirements';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { useQuery } from '@tanstack/react-query';
import { createQueryKey } from 'src/utils/query/createQueryKey';
import { useEffect, useRef } from 'react';

// Check once every 5 minutes (arbitrary number, can update)
const BASE_REFRESH_RATE_MS = 1000 * 60 * 5;

const fetchDeploymentId = async (): Promise<string> => {
	const response = await fetch('/api/deployment');
	if (!response.ok) throw new Error('Failed to fetch deployment');
	const json = await response.json();
	return json.deploymentId ?? '';
};

const useListenForNewDeploys = () => {
	const pathname = usePathname();
	const set = useVelocityStore((s) => s.set);
	const latestDeployId = useVelocityStore((s) => s.latestDeployId);
	const initializedRef = useRef(false);

	// Only check for new deploys on trading and portfolio pages where users might need updates
	const enabled = pathname
		? needsDataRequirement(pathname, 'trading') ||
			needsDataRequirement(pathname, 'portfolio')
		: false;

	const { data: newDeploymentId } = useQuery({
		queryKey: createQueryKey('latestDeployment'),
		queryFn: fetchDeploymentId,
		enabled,
		refetchInterval: BASE_REFRESH_RATE_MS,
		refetchOnWindowFocus: false,
		retry: false,
	});

	useEffect(() => {
		if (!newDeploymentId) return;

		if (!initializedRef.current) {
			initializedRef.current = true;
			set((s) => {
				s.latestDeployId = newDeploymentId;
			});
			return;
		}

		if (newDeploymentId !== latestDeployId) {
			set((s) => {
				s.statusBars.showNewVersionStatusBar = true;
			});
		}
	}, [newDeploymentId, latestDeployId]);
};

export default useListenForNewDeploys;
